import './styles/main.css';
import './styles/mobile-controls.css';
import './styles/arcade.css';
import './styles/modern.css';
import { GameEngine } from './game/engine';
import { FixedStepClock } from './game/loop';
import { scoreDisplay } from './game/scoring';
import { RendererManager } from './rendering/RendererManager';
import { captureSnapshot } from './rendering/RendererInterface';
import { AudioEngine } from './audio/audio-engine';
import { InputManager } from './input/input-manager';
import { bindKeyboard } from './input/keyboard';
import { bindTouch } from './input/touch';
import { GamepadInput } from './input/gamepad';
import { browserDatabase } from './storage/database';
import type { Preferences } from './storage/preferences';
import type { HighScore } from './storage/highscores';
import { AttractMode } from './ui/attract-mode';
import { Menu, type Panel } from './ui/menu';
import { cabinet } from './ui/cabinet';
import { registerPwa } from './pwa/register';
import type { PwaUpdate } from './pwa/updates';

const root=document.querySelector<HTMLDivElement>('#app')!;
document.body.dataset.appState='BOOT';
root.innerHTML=cabinet();
const el=<T extends HTMLElement>(id:string):T=>document.getElementById(id) as T;
const canvas=el<HTMLCanvasElement>('game');
const game=new GameEngine();
const attract=new AttractMode();
const clock=new FixedStepClock();
const database=browserDatabase();
const renderer=new RendererManager(canvas,database.data.preferences,toast,()=>{
  database.savePreferences({...database.data.preferences,graphicsMode:'classic'});menu.refreshGraphics();
});
const audio=new AudioEngine();
const input=new InputManager(d=>{if(activeGame&&!menu.isOpen)game.input(d);},()=>game.clearInput());
let activeGame=false,starting=false,scoreProcessed=false,graphicsChanging=false;
let runningBeforeMenu=false,resumeAfterGraphics=false;
let pendingScore:HighScore|null=null;
let toastTimer=0;
let pwa:PwaUpdate|null=null;
let installPrompt:InstallEvent|null=null;
let previousState='',previousScore=-1;
let previousAppState='BOOT';
let lastAudioState='';
let resizeFrame=0;

interface InstallEvent extends Event { prompt:()=>Promise<void>; userChoice:Promise<{outcome:string}> }

function toast(message:string):void {
  const node=el('toast');node.textContent=message;node.hidden=false;clearTimeout(toastTimer);
  toastTimer=window.setTimeout(()=>{node.hidden=true;},5000);
}
function applyPreferences():void {
  const p=database.data.preferences;document.body.classList.toggle('crt',p.crt);
  audio.configure(p);input.repeatFrames=p.repeatFrames;
  renderer.configure(p);
  el('music-toggle').setAttribute('aria-pressed',String(p.music));
  el('music-toggle').setAttribute('aria-label',p.music?'Musik ausschalten':'Musik einschalten');
  el('mode-label').textContent=`${p.startLives} LIVES · 1 PLAYER`;
  root.querySelector('.machine-top>span:first-child')!.innerHTML=`<i class="status-dot"></i> ${p.startLives===3?'ORIGINAL ARCADE':'ADVANCED'}`;
}
async function preference(key:keyof Preferences,value:unknown):Promise<void> {
  if(key==='graphicsMode'){
    if(graphicsChanging)return;graphicsChanging=true;
    const wasRunning=activeGame&&game.state!=='PAUSED'&&!['GAME_OVER','HIGH_SCORE_ENTRY'].includes(game.state);
    if(wasRunning)pause();else {input.clear();clock.reset();}
    try{
      const mode=await renderer.setMode(value==='modern'?'modern':'classic');
      database.savePreferences({...database.data.preferences,graphicsMode:mode});
      if(menu.isOpen&&runningBeforeMenu)resumeAfterGraphics=true;
      if(wasRunning&&!menu.isOpen){game.resume();await audio.resume();}
    }finally{graphicsChanging=false;clock.reset();}
    return;
  }
  database.savePreferences({...database.data.preferences,[key]:value});applyPreferences();
}
function updateRecord():void {el('local-record').textContent=scoreDisplay(database.highScore);game.highScore=Math.max(game.highScore,database.highScore);}
function pause():void {
  game.pause();input.clear();gamepad.clear();clock.reset();void audio.suspend();
  root.querySelectorAll('.pressed').forEach(e=>e.classList.remove('pressed'));
}
function openPanel(panel:Panel):void {
  if(!menu.isOpen){runningBeforeMenu=activeGame&&!['PAUSED','GAME_OVER','HIGH_SCORE_ENTRY'].includes(game.state);resumeAfterGraphics=false;}
  if(activeGame)pause();else {clock.reset();void audio.suspend();}
  menu.licensed=audio.licensedAvailable;menu.open(panel);
  document.body.dataset.appState=panel==='settings'?'SETTINGS':'MAIN_MENU';
}
async function startGame():Promise<void>{
  if(starting||graphicsChanging)return;
  if(menu.isOpen)menu.close();
  starting=true;
  try{
    await audio.unlock();
    if(audio.error)toast(audio.error);
    if(activeGame&&game.state==='PAUSED'){game.resume();await audio.resume();}
    else if(!activeGame||game.state==='GAME_OVER'||game.state==='HIGH_SCORE_ENTRY'){
      game.highScore=database.highScore;game.start(database.data.preferences.startLives);
      activeGame=true;scoreProcessed=false;pendingScore=null;
    }
    input.clear();clock.reset();canvas.focus({preventScroll:true});
  }finally{starting=false;}
}
async function togglePause():Promise<void>{
  if(menu.isOpen){menu.close();return;}
  if(!activeGame)return;
  if(game.state==='PAUSED'){await audio.unlock();game.resume();clock.reset();canvas.focus({preventScroll:true});}
  else pause();
}
function endGame():void {
  if(!activeGame||['GAME_OVER','HIGH_SCORE_ENTRY'].includes(game.state)){menu.close();toast('Starte eine neue Runde mit START.');return;}
  menu.close();game.transition('GAME_OVER');audio.stopMusic();void audio.resume().then(()=>audio.playEffect('game-over'));
  processGameOver();
}
function processGameOver():void {
  if(scoreProcessed)return;scoreProcessed=true;
  input.clear();
  if(database.qualifies(game.score)){
    pendingScore={initials:'AAA',score:game.score,level:game.level,date:new Date().toISOString(),homes:game.homeCount,
      startLives:game.startingLives as 3|5|7,mode:game.startingLives===3?'ORIGINAL ARCADE':'ADVANCED'};
    game.transition('HIGH_SCORE_ENTRY');menu.open('entry',pendingScore);
  }else toast(`GAME OVER · ${game.score.toLocaleString('de-DE')} Punkte`);
}
const menu=new Menu(database,{
  action:action=>{
    if(action==='start')void startGame();
    if(action==='end')endGame();
    if(action==='dialog-closed'){
      input.clear();clock.reset();
      if(game.state==='HIGH_SCORE_ENTRY'){game.transition('GAME_OVER');pendingScore=null;}
      if(game.state==='PAUSED'){
        if(resumeAfterGraphics&&runningBeforeMenu&&!graphicsChanging){game.resume();void audio.resume();}
        else void audio.suspend();
      }
      resumeAfterGraphics=false;runningBeforeMenu=false;
      document.body.dataset.appState=activeGame?game.state:'TITLE';
      if(activeGame)canvas.focus({preventScroll:true});
    }
    if(action==='native-install'){
      if(installPrompt)void installPrompt.prompt().then(async()=>{await installPrompt!.userChoice;installPrompt=null;});
      else toast(window.matchMedia('(display-mode: standalone)').matches?'Die App läuft bereits im Standalone-Modus.':'In Safari: Teilen → Zum Home-Bildschirm → Hinzufügen.');
    }
  },
  preference,
  soundTest:()=>{void audio.unlock().then(()=>{audio.playEffect('start');menu.licensed=audio.licensedAvailable;if(audio.error)toast(audio.error);});},
  save:initials=>{
    if(!pendingScore)return;database.addScore({...pendingScore,initials});pendingScore=null;updateRecord();game.transition('GAME_OVER');
    toast(database.warning?'Punktestand nur für diese Sitzung gespeichert.':'HIGH SCORE SAVED · '+initials);
  },
});
const gamepad=new GamepadInput(input,()=>{if(activeGame)void togglePause();else void startGame();});
audio.onInterruption=pause;
bindKeyboard(input,{start:()=>void startGame(),pause:()=>void togglePause(),music:()=>toggleMusic()});
bindTouch(root,canvas,input,()=>database.data.preferences.controls==='swipe',()=>{
  if(activeGame&&game.state==='PLAYING'&&!audio.unlocked)void audio.unlock();
});
function toggleMusic():void {
  void preference('music',!database.data.preferences.music);
  if(database.data.preferences.music)void audio.unlock();
}
// One primary control owns start, pause, resume and retry. Keeping separate
// start/resume and pause/resume buttons made both show RESUME while paused.
el('start-button').addEventListener('click',()=>{
  if(starting||graphicsChanging)return;
  if(activeGame&&!['PAUSED','GAME_OVER','HIGH_SCORE_ENTRY'].includes(game.state))void togglePause();
  else void startGame();
});
el('menu-button').addEventListener('click',()=>openPanel('menu'));
el('music-toggle').addEventListener('click',toggleMusic);
root.querySelectorAll<HTMLButtonElement>('[data-panel]').forEach(button=>button.addEventListener('click',()=>openPanel(button.dataset.panel as Panel)));
root.querySelector('.brand')?.addEventListener('click',e=>{
  e.preventDefault();if(activeGame&&!['GAME_OVER','HIGH_SCORE_ENTRY'].includes(game.state)){openPanel('menu');return;}
  activeGame=false;audio.stopMusic();void audio.suspend();input.clear();clock.reset();
});
el('update-button').addEventListener('click',()=>{
  if(!pwa?.apply(!activeGame||['GAME_OVER','HIGH_SCORE_ENTRY'].includes(game.state)))toast('Update nach dem Ende der aktuellen Runde verfügbar. Highscore zuerst speichern.');
});
window.addEventListener('beforeinstallprompt',e=>{e.preventDefault();installPrompt=e as InstallEvent;});
document.addEventListener('visibilitychange',()=>{
  if(document.hidden){pause();renderer.suspend();}else {clock.reset();renderer.resume();}
});
window.addEventListener('blur',pause);
window.addEventListener('pagehide',()=>{pause();renderer.suspend();});
window.addEventListener('pageshow',()=>{clock.reset();renderer.resume();});
document.addEventListener('contextmenu',e=>{if((e.target as Element).closest('.machine'))e.preventDefault();});
// iOS Safari still permits browser zoom from its UI. Game pointers and all
// control gestures are captured without disabling accessibility zoom globally.
document.addEventListener('gesturestart',e=>{if((e.target as Element).closest('.machine'))e.preventDefault();},{passive:false});

function fitScreen():void {
  const mobile=window.innerWidth<=740;
  const viewport=window.visualViewport?.height||window.innerHeight;
  const bodyStyle=getComputedStyle(document.body);
  const safeTop=parseFloat(bodyStyle.paddingTop)||0,safeBottom=parseFloat(bodyStyle.paddingBottom)||0;
  let maxWidth:number,maxHeight:number;
  if(mobile){
    maxWidth=window.innerWidth-(parseFloat(bodyStyle.paddingLeft)||12)-(parseFloat(bodyStyle.paddingRight)||12)-24;
    const tight=window.innerHeight<=700;
    maxHeight=viewport-safeTop-safeBottom-(tight?46:55)-(tight?23:27)-(tight?234:256);
  }else{
    maxWidth=Math.min(448,(window.innerWidth-420)*0.58);
    maxHeight=Math.max(300,viewport-355);
  }
  let width=Math.max(150,Math.min(maxWidth,maxHeight*224/256));
  const dpr=window.devicePixelRatio||1;
  const integerWidth=Math.floor(width*dpr/224)*224/dpr;
  if(integerWidth>=width*0.9)width=integerWidth;
  else width=Math.floor(width*dpr)/dpr;
  document.documentElement.style.setProperty('--screen-width',`${width}px`);
}
function queueResize():void {cancelAnimationFrame(resizeFrame);resizeFrame=requestAnimationFrame(fitScreen);}
window.addEventListener('resize',queueResize);window.visualViewport?.addEventListener('resize',queueResize);

function draw(now:number):void {
  if(!document.hidden){
    if(!menu.isOpen&&!graphicsChanging){
      gamepad.poll();
      clock.advance(now,()=>{
        if(activeGame){input.tick();game.step();}
        else attract.step();
      });
    }else clock.reset();
    if(activeGame){
      const events=game.drainEvents();
      for(const event of events)audio.event(event);
      if(game.state==='GAME_OVER')processGameOver();
      const snapshot=captureSnapshot(game);renderer.events(events,snapshot);
      renderer.draw(snapshot,{title:false,wallTime:now,reducedMotion:reducedMotion.matches});
      if(!menu.isOpen)audio.update(game.state);
    }else {attract.engine.highScore=database.highScore;renderer.draw(captureSnapshot(attract.engine),{title:true,wallTime:now,reducedMotion:reducedMotion.matches});}
    const state=activeGame?game.state:'TITLE';
    const appState=menu.isOpen?menu.appState:starting&&!audio.unlocked?'AUDIO_UNLOCK':activeGame?game.state:now<8000?'TITLE':'ATTRACT_MODE';
    if(appState!==previousAppState){previousAppState=appState;document.body.dataset.appState=appState;}
    if(state!==previousState){
      previousState=state;canvas.dataset.state=state;
      const action=state==='PAUSED'?'resume':state==='GAME_OVER'||state==='HIGH_SCORE_ENTRY'?'retry':activeGame?'pause':'start';
      const labels={start:'START',pause:'PAUSE',resume:'RESUME',retry:'RETRY'};
      const accessible={start:'Spiel starten',pause:'Spiel pausieren',resume:'Spiel fortsetzen',retry:'Neue Runde starten'};
      el('start-label').textContent=labels[action];
      el('start-button').dataset.controlState=action;
      el('start-button').setAttribute('aria-label',accessible[action]);
      el('update-button').toggleAttribute('disabled',activeGame&&!['GAME_OVER'].includes(state));
      el('live-status').textContent=state==='PAUSED'?'Spiel pausiert.':state==='GAME_OVER'?`Game Over. ${game.score} Punkte.`:state==='PLAYING'?'Spiel läuft.':'';
    }
    if(activeGame&&game.score!==previousScore){previousScore=game.score;canvas.dataset.score=String(game.score);el('local-record').textContent=scoreDisplay(Math.max(database.highScore,game.highScore));}
    const audioState=audio.context?.state||'locked';
    if(lastAudioState!==audioState){lastAudioState=audioState;document.body.dataset.audioState=audioState;}
  }else clock.reset();
  requestAnimationFrame(draw);
}
const reducedMotion=window.matchMedia('(prefers-reduced-motion: reduce)');
applyPreferences();updateRecord();fitScreen();
renderer.draw(captureSnapshot(attract.engine),{title:true,wallTime:0,reducedMotion:reducedMotion.matches});
requestAnimationFrame(draw);
if(database.data.preferences.graphicsMode==='modern')void preference('graphicsMode','modern');
if(!database.data.preferences.quickStart)openPanel('menu');
if(database.warning)toast('Lokaler Speicher ist eingeschränkt. Highscores bleiben eventuell nur für diese Sitzung.');
if(import.meta.env.DEV)el('offline-label').textContent='DEV MODE · OFFLINE IM PRODUKTIONSBUILD';
void registerPwa({
  offline:state=>{
    el('offline-status').classList.toggle('ready',state.ready);
    el('offline-label').textContent=state.ready?'READY TO PLAY OFFLINE':'OFFLINE-CACHE NOCH NICHT VOLLSTÄNDIG';
    el('offline-status').dataset.version=state.version;
    el('offline-status').dataset.cached=String(state.count);
  },
  update:()=>{el('update-button').hidden=false;el<HTMLButtonElement>('update-button').disabled=activeGame&&game.state!=='GAME_OVER';},
  error:message=>{el('offline-label').textContent='OFFLINE-EINRICHTUNG ERNEUT VERSUCHEN';toast(message);},
}).then(value=>{pwa=value;});
